import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowRight, ArrowUpRight, CalendarDays, Globe, Instagram, Mail, Phone, X } from 'lucide-react'
import {
  professionals,
  suiteDirectory,
  type Professional,
} from '../data/site'
import { imageSize } from '../data/images'

const asset = (name: string) => `${import.meta.env.BASE_URL}images/${name}`

function phoneHref(phone: string) {
  return `tel:+1${phone.replace(/\D/g, '')}`
}

function ProfessionalDialog({
  professional,
  onDismiss,
  returnFocus,
}: {
  professional: Professional
  onDismiss: () => void
  returnFocus: HTMLElement | null
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const closingRef = useRef(false)
  const [isClosing, setIsClosing] = useState(false)

  const requestClose = useCallback(() => {
    if (closingRef.current) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onDismiss()
      return
    }

    closingRef.current = true
    setIsClosing(true)
    closeTimerRef.current = window.setTimeout(onDismiss, 220)
  }, [onDismiss])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    dialog.showModal()
    document.body.classList.add('dialog-open')
    const focusFrame = window.requestAnimationFrame(() => dialog.focus())
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        requestClose()
      }
    }
    document.addEventListener('keydown', handleEscape)

    return () => {
      window.cancelAnimationFrame(focusFrame)
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current)
      document.body.classList.remove('dialog-open')
      document.removeEventListener('keydown', handleEscape)
      returnFocus?.focus()
    }
  }, [requestClose, returnFocus])

  const hasPublicLinks = Boolean(
    professional.phone
      || professional.email
      || professional.bookingUrl
      || professional.websiteUrl
      || professional.socialLinks?.length,
  )

  return (
    <dialog
      ref={dialogRef}
      className={`profile-dialog${isClosing ? ' is-closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`profile-${professional.suite}-title`}
      tabIndex={-1}
      onCancel={(event) => {
        event.preventDefault()
        requestClose()
      }}
      onClose={requestClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) requestClose()
      }}
    >
      <div className="profile-dialog__panel">
        <button className="profile-dialog__close" type="button" onClick={requestClose} aria-label={`Close ${professional.name} profile`}>
          <X aria-hidden="true" />
        </button>

        <div className="profile-dialog__media">
          {professional.image ? (
            <img src={asset(professional.image)} alt={`${professional.name}, ${professional.title}`} {...imageSize(professional.image)} />
          ) : (
            <div className="profile-placeholder" aria-hidden="true">
              {professional.name
                .split(/\s|&/)
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0])
                .join('')}
            </div>
          )}
          <span>Suite {professional.suite}</span>
        </div>

        <div className="profile-dialog__content">
          <p className="eyebrow"><span>Suite {professional.suite}</span>Independent professional</p>
          <h2 id={`profile-${professional.suite}-title`}>{professional.name}</h2>
          {professional.businessName && (
            <p className="profile-dialog__business">{professional.businessName}</p>
          )}
          <p className="profile-dialog__title">{professional.title}</p>

          {professional.bio?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}

          {hasPublicLinks && <div className="profile-dialog__actions" aria-label={`${professional.name} contact and booking links`}>
            {professional.bookingUrl && (
              <a className="button" href={professional.bookingUrl} target="_blank" rel="noopener noreferrer">
                <CalendarDays aria-hidden="true" /> Book appointment
              </a>
            )}
            {professional.phone && (
              <a className={professional.bookingUrl ? 'button button--outline' : 'button'} href={phoneHref(professional.phone)}>
                <Phone aria-hidden="true" /> Call {professional.phone}
              </a>
            )}
            {professional.email && (
              <a className="text-link" href={`mailto:${professional.email}`}>
                <Mail aria-hidden="true" /> Email
              </a>
            )}
            {professional.websiteUrl && (
              <a className="text-link" href={professional.websiteUrl} target="_blank" rel="noopener noreferrer">
                <Globe aria-hidden="true" /> Website <ArrowUpRight aria-hidden="true" />
              </a>
            )}
            {professional.socialLinks?.map((socialLink) => (
              <a className="text-link" href={socialLink.url} key={socialLink.url} target="_blank" rel="noopener noreferrer">
                <Instagram aria-hidden="true" /> {socialLink.label} <ArrowUpRight aria-hidden="true" />
              </a>
            ))}
          </div>}
        </div>
      </div>
    </dialog>
  )
}

function ProfessionalCard({
  professional,
  onOpen,
}: {
  professional: Professional
  onOpen: (professional: Professional, trigger: HTMLButtonElement) => void
}) {
  return (
    <article className="professional-card">
      <button
        className="professional-card__trigger"
        type="button"
        aria-haspopup="dialog"
        aria-label={`View ${professional.name} profile`}
        onClick={(event) => onOpen(professional, event.currentTarget)}
      >
        <span className="professional-card__media">
          {professional.image ? (
            <img
              src={asset(professional.image)}
              alt={`${professional.name}, ${professional.title}`}
              {...imageSize(professional.image)}
              loading="lazy"
            />
          ) : (
            <span className="profile-placeholder" aria-hidden="true">
              {professional.name
                .split(/\s|&/)
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0])
                .join('')}
            </span>
          )}
          <span>Suite {professional.suite}</span>
        </span>
        <span className="professional-card__body">
          <span className="professional-card__index">{String(professional.suite).padStart(2, '0')} / 08</span>
          <h3 className="professional-card__name">{professional.name}</h3>
          {professional.businessName && <span className="professional-card__business">{professional.businessName}</span>}
          <span className="professional-card__title">{professional.title}</span>
          {professional.bio?.[0] && (
            <span className="professional-card__bio">{professional.bio[0]}</span>
          )}
          <span className="professional-card__cta">View profile <ArrowRight aria-hidden="true" /></span>
        </span>
      </button>
    </article>
  )
}

export default function ProfessionalDirectory({ preview = false }: { preview?: boolean }) {
  const [selected, setSelected] = useState<Professional | null>(null)
  const [returnFocus, setReturnFocus] = useState<HTMLElement | null>(null)

  const openProfile = (professional: Professional, trigger: HTMLButtonElement) => {
    setReturnFocus(trigger)
    setSelected(professional)
  }

  if (preview) {
    return (
      <>
        <div className="professional-grid">
          {professionals.filter((professional) => professional.image).map((professional) => (
            <ProfessionalCard key={professional.suite} professional={professional} onOpen={openProfile} />
          ))}
        </div>
        {selected && (
          <ProfessionalDialog
            professional={selected}
            onDismiss={() => setSelected(null)}
            returnFocus={returnFocus}
          />
        )}
      </>
    )
  }

  return (
    <>
      <div className="directory-grid">
        {suiteDirectory.map(({ suite, professional }) =>
          professional ? (
            <ProfessionalCard key={suite} professional={professional} onOpen={openProfile} />
          ) : (
            <article className="professional-card professional-card--pending" key={suite}>
              <div className="professional-card__media">
                <div className="profile-placeholder" aria-hidden="true">DS</div>
                <span>Suite {suite}</span>
              </div>
              <div className="professional-card__body">
                <p className="professional-card__index">{String(suite).padStart(2, '0')} / 08</p>
                <h3>Suite {suite}</h3>
                <p className="professional-card__title">Directory details coming soon</p>
              </div>
            </article>
          ),
        )}
      </div>
      {selected && (
        <ProfessionalDialog
          professional={selected}
          onDismiss={() => setSelected(null)}
          returnFocus={returnFocus}
        />
      )}
    </>
  )
}
